import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, isSupabaseConfigured } from '../config/supabase';
import { ensureUserProfile } from '../services/userProfileService';

/**
 * Single source of truth for authentication.
 *
 * Previously `useAuth` and `useEmailAuth` were plain hooks, so EVERY component
 * that called one got its own independent copy of the state - its own
 * `supabase.auth.getSession()`, its own `onAuthStateChange` subscription, and
 * its own `user_profiles` / `email_users` profile fetches. With 8 direct
 * `useAuth()` call sites and 10 `useEmailAuth()` call sites (each of which
 * called `useAuth` internally), a mounted tree registered many duplicate auth
 * listeners and repeated the same profile queries.
 *
 * That duplication is why the codebase needed `forceUpdate` counters and a
 * window `auth-force-update` CustomEvent listened to in components: machinery
 * whose only job was pushing separate copies of the state back into agreement.
 * All of it is gone - one provider means a change re-renders every consumer
 * directly, and the event's remaining job (clearing the email-only identity)
 * disappeared along with that identity.
 *
 * `profileUpdateTrigger` is NOT part of that machinery and stays: UserProfile
 * calls triggerProfileRefresh() after saving to force a re-read of user_profiles,
 * which is a genuine refetch rather than a sync hack.
 *
 * The implementations below are unchanged - they have simply moved here and
 * now run ONCE, inside AuthProvider. `useAuth()` and `useEmailAuth()` keep
 * their exact previous return shapes, so no consumer needed to change.
 */

const AuthContext = createContext(null);

/* -------------------------------------------------------------------------
 * Supabase auth (formerly src/hooks/useAuth.js)
 * ---------------------------------------------------------------------- */
const useAuthState = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forceSignedOut, setForceSignedOut] = useState(false);

  // Memoize debug mode to prevent unnecessary re-renders
  const debugMode = useMemo(
    () => typeof window !== 'undefined' && localStorage.getItem('admin_debug_mode') === 'true',
    [] // Only recalculate on mount
  );

  useEffect(() => {
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      setLoading(false);
      setError(null); // Don't set error for unconfigured Supabase - it's handled by components
      return;
    }

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setUser(session?.user || null);
      } catch (err) {
        console.error('Error getting session:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (debugMode) {
          console.log('🔍 Auth state changed:', event, session?.user?.email);
          console.log('  forceSignedOut flag:', forceSignedOut);
          console.log('  session?.user exists:', !!session?.user);
        }

        // Don't update user state if we've forced a sign out
        if (forceSignedOut && session?.user) {
          if (debugMode) console.log('❌ Ignoring auth state change due to forced sign out');
          return;
        }

        if (debugMode) console.log('✅ Processing auth state change');

        // Check for project mismatch before setting user
        if (session?.user) {
          try {
            // Decode the JWT token to check the issuer
            const token = session.access_token;
            const payload = JSON.parse(atob(token.split('.')[1]));

            // Check if the token is from the correct Supabase project
            if (payload.iss && !payload.iss.includes('bjhtlokaafsnoscgecct')) {
              console.error('❌ JWT ISSUER MISMATCH detected in auth state change!');
              console.error('Token issuer:', payload.iss);
              console.error('Expected project: bjhtlokaafsnoscgecct');

              // Sign out the user automatically
              console.log('🔄 Auto-signing out user due to project mismatch');
              await supabase.auth.signOut();

              setUser(null);
              setError('Account mismatch: Your account was created on a different server. Please create a new account on this server.');
              setLoading(false);
              return;
            }
          } catch (tokenError) {
            console.warn('⚠️ Could not validate token issuer:', tokenError);
            // Continue with normal flow if token validation fails
          }
        }

        setUser(session?.user || null);
        setLoading(false);
        setError(null);

        // Reset force signed out flag when successfully signed in
        if (session?.user) {
          if (debugMode) console.log('🔄 Resetting forceSignedOut flag due to new session');
          setForceSignedOut(false);
        }

        // First sign-in through any method (passcode, Google, password) gets a
        // user_profiles row, carrying over an old Quick Join name/avatar if the
        // address matches. Idempotent and never throws.
        if (session?.user && event === 'SIGNED_IN') {
          ensureUserProfile(session.user);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceSignedOut]);



  /* ---- Passwordless -------------------------------------------------------
   * Step 1: signInWithPasscode emails a 6-digit code.
   * Step 2: verifyPasscode exchanges that code for a session.
   *
   * A code rather than a magic link is deliberate. This app has no router, and
   * its one existing redirect-based flow (password reset -> /reset-password)
   * silently did nothing because no route handled it. A typed code never leaves
   * the page that requested it, and works when mail is read on another device.
   *
   * Requires SMTP, and requires the Supabase "Magic Link" email template to
   * render {{ .Token }} rather than {{ .ConfirmationURL }} - otherwise the user
   * receives a link and has no code to type.
   * ---------------------------------------------------------------------- */
  const signInWithPasscode = useCallback(async (email) => {
    if (!isSupabaseConfigured()) {
      const err = new Error('Supabase is not configured. Please set up your credentials in Settings.');
      setError(err.message);
      return { data: null, error: err };
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { shouldCreateUser: true } // no separate sign-up step any more
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error sending sign-in code:', err);
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyPasscode = useCallback(async (email, token) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: token.trim(),
        type: 'email'
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error verifying sign-in code:', err);
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  // Google needs no SMTP at all, so it stays available even if email delivery
  // breaks - which is exactly the failure that motivated this work.
  const signInWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      const err = new Error('Supabase is not configured. Please set up your credentials in Settings.');
      setError(err.message);
      return { data: null, error: err };
    }

    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error starting Google sign-in:', err);
      setError(err.message);
      return { data: null, error: err };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is configured
      if (!isSupabaseConfigured()) {
        setUser(null);
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err.message);

      // Force clear user state even if signOut fails
      setUser(null);
      setForceSignedOut(true); // Flag to override any auth state listener
      setError(null); // Clear any previous errors

      // Also set a timeout to prevent auth listener from overriding for a few seconds
      setTimeout(() => {
        console.log('Allowing auth listener to resume after forced signout');
        setForceSignedOut(false);
      }, 3000); // 3 second grace period

      // Clear only auth-related localStorage (not config)
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          // Clear ONLY auth tokens, not config
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
              key.startsWith('supabase.auth.token') ||
              (key.startsWith('sb-') && key.includes('auth-token'))
            )) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => localStorage.removeItem(key));
          console.log('Cleared Supabase auth tokens only:', keysToRemove);
        }
      } catch (storageErr) {
        console.error('Error clearing localStorage:', storageErr);
      }

      console.log('Force signed out flag set, user should appear signed out');

      // Every consumer re-renders from the provider, so no counter is needed to
      // push the new state around. The `auth-force-update` event that used to be
      // dispatched here existed solely to tell the email-only identity to clear
      // itself; there is no such identity any more, and it had no other listener.
    } finally {
      setLoading(false);
    }
  }, []);


  const isAuthenticated = useMemo(() => !!user && !forceSignedOut, [user, forceSignedOut]);

  return useMemo(() => ({
    user,
    loading,
    error,
    // Passwordless (current)
    signInWithPasscode,
    verifyPasscode,
    signInWithGoogle,
    signOut,
    isAuthenticated
  }), [
    user, loading, error,
    signInWithPasscode, verifyPasscode, signInWithGoogle,
    signOut, isAuthenticated
  ]);
};

/* -------------------------------------------------------------------------
 * Current user + profile
 *
 * This used to also carry an "email-only" (Quick Join) identity, held in
 * localStorage, that let anyone claim any address without verification. Sign-in
 * is now passwordless for everyone, so there is a single verified identity and
 * that whole parallel model is gone: emailUser, handleEmailAuth,
 * signOutEmailUser, refreshEmailUser, the email_users profile cache, and the
 * updateUserProfile writer that maintained it.
 *
 * The hook keeps its old name and its isUserAuthenticated field so the ~10 call
 * sites did not all have to change; isUserAuthenticated is now simply a mirror
 * of isAuthenticated. Renaming both is worth doing, separately.
 * ---------------------------------------------------------------------- */
const useEmailAuthState = (authData) => {
  const { user } = authData;
  const [profileUpdateTrigger, setProfileUpdateTrigger] = useState(0);
  const [cachedAuthUserProfile, setCachedAuthUserProfile] = useState(null);

  // Anyone still carrying a Quick Join identity from before the migration is
  // signed out; they sign in properly instead, and ensureUserProfile carries
  // their name and avatar across on first sign-in.
  useEffect(() => {
    if (localStorage.getItem('current_email_user')) {
      console.log('Clearing legacy Quick Join identity - sign in to continue');
      localStorage.removeItem('current_email_user');
    }
  }, []);

  // Load fresh profile data for the signed-in user
  useEffect(() => {
    const loadAuthUserProfile = async () => {
      if (authData.isAuthenticated && user?.id) {
        try {
          const { data: profileData, error } = await supabase
            .from('user_profiles')
            .select('full_name, avatar_url')
            .eq('user_id', user.id)
            .single();

          if (!error && profileData) {
            setCachedAuthUserProfile({
              name: profileData.full_name || user.email,
              profilePicture: profileData.avatar_url || null
            });
          } else {
            setCachedAuthUserProfile(null);
          }
        } catch (error) {
          console.warn('Failed to load user profile:', error);
          setCachedAuthUserProfile(null);
        }
      } else {
        setCachedAuthUserProfile(null);
      }
    };

    loadAuthUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.isAuthenticated, user?.id, profileUpdateTrigger]);

  const getCurrentUser = useCallback(() => {
    if (!authData.isAuthenticated || !user) return null;

    const profileData = cachedAuthUserProfile || {
      name: user.user_metadata?.full_name || user.user_metadata?.name || user.email,
      profilePicture: user.user_metadata?.avatar_url || user.user_metadata?.picture || null
    };

    return {
      userId: user.id,
      email: user.email,
      name: profileData.name,
      profilePicture: profileData.profilePicture,
      isEmailOnly: false, // retained so adminAccess keeps failing closed
      needsProfileRefresh: !cachedAuthUserProfile
    };
  }, [authData.isAuthenticated, user, cachedAuthUserProfile]);

  const isUserAuthenticated = authData.isAuthenticated;

  const getRememberedName = useCallback(() => {
    return getCurrentUser()?.name || '';
  }, [getCurrentUser]);

  // Called after saving a profile, to force a re-read of user_profiles.
  const triggerProfileRefresh = useCallback(() => {
    setProfileUpdateTrigger((prev) => prev + 1);
  }, []);

  return useMemo(() => ({
    isUserAuthenticated,
    getCurrentUser,
    getRememberedName,
    triggerProfileRefresh
  }), [isUserAuthenticated, getCurrentUser, getRememberedName, triggerProfileRefresh]);
};

/* -------------------------------------------------------------------------
 * Provider + consumer hooks
 * ---------------------------------------------------------------------- */
export const AuthProvider = ({ children }) => {
  const auth = useAuthState();
  const emailAuth = useEmailAuthState(auth);

  const value = useMemo(() => ({ auth, emailAuth }), [auth, emailAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuthContext = (hookName) => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(`${hookName} must be used inside <AuthProvider>. Wrap the app in src/index.js.`);
  }
  return ctx;
};

export const useAuth = () => useAuthContext('useAuth').auth;

export const useEmailAuth = () => useAuthContext('useEmailAuth').emailAuth;

export default AuthContext;
